import axios from 'axios';
import { BriefingResourceDetail } from '@/shared/model/briefing-widget.model';

const BASE_URL = '/api/briefing';

interface HomeDiscoveryParams {
  documentType?: string[];
  contentType?: string[];
  startDate?: string | Date;
  endDate?: string | Date;
  language?: string;
  dataSourceChannel?: string[];
  dataSourceType?: string[];
  loadMoreDiscovery: boolean;
  loadMoreReport: boolean;
  page?: number;
  size?: number;
  sessionId?: string;
}

export const getHomeDiscovery = async (params?: HomeDiscoveryParams) => {
  const { page, size, loadMoreReport = true, loadMoreDiscovery = true, ...otherParams } = params;

  const { status, data } = await axios.post(
    `${BASE_URL}/home/getAllBriefingData`,
    {
      ...otherParams,
      loadMoreDiscovery,
      loadMoreReport,
    },
    {
      params: { page, size },
    }
  );

  return data;
};

interface BriefingDiscoveryParams {
  bpId: string;
  briefingPrivacy: null | string;
  briefingType: null | string[];
  documentType: string[];
  language: 'ko' | 'en';
  loadMoreDiscovery: boolean;
  loadMoreReport: boolean;
  page: number;
  size: number;
}

export const getBriefingDiscovery = async (params?: BriefingDiscoveryParams) => {
  const { page, size, loadMoreReport = true, loadMoreDiscovery = true, ...otherParams } = params;

  const { status, data } = await axios.post(
    `${BASE_URL}/socialBriefing/getAllBriefingData`,
    {
      ...otherParams,
      loadMoreDiscovery,
      loadMoreReport,
    },
    {
      params: { page, size: 40 },
    }
  );

  return data;
};

interface OnBookmark {
  requestId: number | string;
  originType: string;
  parentDocDetail: BriefingResourceDetail;
  userKey?: string;
}

export const onBookmark = async (params: OnBookmark) => {
  const { parentDocDetail, ...others } = params;
  const payload = { ...parentDocDetail, doc_type: 3 };

  const { data } = await axios.post(`${BASE_URL}/briefingResource/saveUserResource`, {
    ...params,
    parentDocDetail: payload,
  });

  if (!data) {
    return false;
  }

  return data.success;
};

export const offBookmark = async (requestId: any, parentDocId: string, userKey?: string) => {
  const { data } = await axios.post(
    `${BASE_URL}/briefingResource/unSaveUserResource`,
    {},
    {
      params: {
        requestId,
        parentDocId,
        userKey,
      },
    }
  );

  if (!data) {
    return false;
  }

  return data.success;
};

interface HideDiscoveryParams {
  briefingPageId: string;
  id: string;
  isDeleted: boolean;
  parentDocId: string;
  typeDocument: string;
}

interface HideDiscoveryExcludeReport {
  isBookmarked: boolean;
  isLiked: boolean;
  similar_hash: string;
  userKey: string;
}

export const onHideReport = async (params: HideDiscoveryParams) => {
  // report
  await axios.post(`${BASE_URL}/feedUpdateStatus`, { ...params }, {});
};

export const onHideNotReport = async (params: HideDiscoveryParams & HideDiscoveryExcludeReport) => {
  await axios.post(
    `${BASE_URL}/hideCardInHome`,
    {
      ...params,
    },
    {}
  );
};

interface SaveBriefingResource {
  documents: any[];
  isUpdate?: boolean;
  saveType?: string;
  reportTypes: string[];
  reportId?: string;
  privateType?: string;
}

export const saveBriefingResource = async (params: SaveBriefingResource) => {
  let willUpdate = false;

  const { isUpdate, saveType, reportTypes, reportId, privateType, documents } = params;

  willUpdate = !isUpdate;

  let apiUrl = `${BASE_URL}/briefingResource/saveBriefingResource?isUpdate=${willUpdate}`;

  if (saveType && saveType.trim().length > 0) {
    apiUrl = apiUrl.concat(`&saveType=${saveType}`);
  }

  if (reportTypes && reportTypes.length > 0) {
    apiUrl = apiUrl.concat(`&reportTypes=${reportTypes}`);
  }

  if (privateType && privateType.length > 0) {
    apiUrl = apiUrl.concat('&privateType=' + privateType);
  }

  if (reportId && reportId?.length > 0) {
    apiUrl = apiUrl.concat('&reportId=' + reportId);
  }

  const res = await axios.post(apiUrl, documents);

  if (!res) {
    return false;
  }

  const { data } = res;
  return data.body.result === 'success';
};

export const getDiscoveryNotDiscoveryMenu = async (params: any) => {
  const { page, size, ...others } = params;

  const { data } = await axios.post(
    `${BASE_URL}/myBriefing/getAllBriefingData`,
    {
      ...others,
    },
    { params: { page, size } }
  );

  return data;
};

export const getCompanyDiscovery = async (params: any) => {
  const { page, size, ...others } = params;

  const response = await axios.post(`${BASE_URL}/company/getAllBriefingData`, others, {
    params: {
      page,
      size,
    },
  });

  if (!response) {
    return [];
  }

  return response.data;
};

interface GetAgent {
  userKey: string;
  language?: string[];
}

export const getAgent = async (params: GetAgent) => {
  console.log(params);
  const response = await axios.post(`${BASE_URL}/socialBriefing/getAllFeaturedBriefings`, params);

  if (!response) {
    return null;
  }

  return response.data;
};

interface GetReportComments {
  // category: 'REPORT'
  referenceId: string; // report id
  userId: string; // user id
  page: number;
  size: number;
}

export const getReportComments = async (params: GetReportComments) => {
  const response = await axios
    .get('/api/memo/get-memo-tree', {
      params: { category: 'REPORT', ...params },
    })
    .catch(() => {
      return { data: [] };
    });

  if (!response) {
    return [];
  }

  return response.data;
};

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
const MAX_TOTAL_SIZE = 100 * 1024 * 1024; // 100MB in bytes

function validateFilesSize(files) {
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const MAX_TOTAL_SIZE = 100 * 1024 * 1024; // 100MB

  let totalSize = 0;

  for (const file of files) {
    if (file.size > MAX_FILE_SIZE) {
      console.error(`파일 "${file.name}" 이(가) 10MB를 초과합니다.`);
      return false;
    }
    totalSize += file.size;
  }

  if (totalSize > MAX_TOTAL_SIZE) {
    console.error(`파일 총 용량이 100MB를 초과합니다.`);
    return false;
  }

  return true;
}

export async function createMemo({ memo, multipartFiles }) {
  if (!validateFilesSize(multipartFiles)) {
    return false;
  }

  const formData = new FormData();

  formData.append('memo', JSON.stringify(memo));
  multipartFiles.forEach(file => {
    formData.append('multipartFile', file);
  });

  // eslint-disable-next-line no-useless-catch
  try {
    const response = await axios.post('/api/memo/create-memo', formData, {
      headers: {
        accept: '*/*',
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function deleteMemo({ id, userId }) {
  try {
    const response = await axios.post('/api/memo/delete-memo', null, {
      params: {
        id,
        userId,
      },
    });

    return response.data;
  } catch (error) {
    console.error('삭제 실패:', error);
    throw error;
  }
}

export async function likeMemo(params: any) {
  const queryString = new URLSearchParams(params).toString();

  const response = await axios.post(`/api/memo/like?${queryString}`, {});

  if (!response) {
    return false;
  }

  return response.data;
}

export async function updateMemo({ memo, multipartFiles = [] }) {
  if (!validateFilesSize(multipartFiles)) {
    return false;
  }

  const formData = new FormData();

  formData.append('memo', JSON.stringify(memo));
  multipartFiles.forEach(file => {
    formData.append('multipartFile', file);
  });

  // eslint-disable-next-line no-useless-catch
  try {
    const response = await axios.post('/api/memo/update-memo', formData, {
      headers: {
        accept: '*/*',
        'Content-Type': 'multipart/form-data',
      },
    });

    if (!response) {
      return false;
    }

    return response.data;
  } catch (error) {
    throw error;
  }
}
